import { Head, Link } from '@inertiajs/react'
import SuperAdminLayout from '../_SuperAdminLayout'
import Filters, { ActivityFilters, OrgOption } from './_Filters'

interface CategoryAction {
  action: string
  count: number
}

interface CategoryRow {
  category: string
  total: number
  actions: CategoryAction[]
}

interface OrgSplitRow {
  organization_id: number | null
  organization_name: string
  count: number
}

interface FeedItem {
  id: number
  action: string
  target_type: string | null
  target_name: string | null
  organization_name: string | null
  created_at: string
}

interface CurrentStateOrg {
  organization_id: number
  organization_name: string
  guide_user_privileges: boolean
  open_action_items: number
  open_topics: number
  active_rocks: number
}

interface CompanyRollupOrg {
  organization_id: number
  organization_name: string
  meetings_started: number
  active_rocks: number
  active_users: number
}

interface Detail {
  by_category: CategoryRow[]
  by_organization: OrgSplitRow[]
  recent_activity: FeedItem[]
  company_rollup: {
    organizations: CompanyRollupOrg[]
    totals: {
      meetings_started: number
      active_rocks: number
      active_users: number
    }
  }
  current_state: {
    account_created_at: string
    organizations: CurrentStateOrg[]
    totals: {
      organizations_guided: number
      open_action_items: number
      open_topics: number
      active_rocks: number
    }
  }
}

interface Props {
  guide: { id: number; full_name: string; email: string }
  detail: Detail
  filters: ActivityFilters
  organizations: OrgOption[]
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit'
  })
}

const humanize = (s: string) => s.replace(/_/g, ' ')

export default function GuideActivityShow({ guide, detail, filters, organizations }: Props) {
  const { by_category, by_organization, recent_activity, current_state, company_rollup } = detail

  return (
    <SuperAdminLayout>
      <Head title={`SuperAdmin · Guide Activity · ${guide.full_name}`} />
      <div className="mb-6">
        <Link href="/super_admin/guide_activity" className="text-sm text-[#ED1C24] hover:underline">
          ← All guides
        </Link>
        <h1 className="text-2xl font-bold text-text-primary mt-1">{guide.full_name}</h1>
        <div className="text-sm text-text-muted">{guide.email}</div>
      </div>

      <Filters path={`/super_admin/guide_activity/${guide.id}`} filters={filters} organizations={organizations} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Stat label="Orgs guided" value={current_state.totals.organizations_guided} />
        <Stat label="Open action items" value={current_state.totals.open_action_items} />
        <Stat label="Open topics" value={current_state.totals.open_topics} />
        <Stat label="Active rocks" value={current_state.totals.active_rocks} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card title="Activity by category">
          {by_category.every(c => c.total === 0) ? (
            <Empty text="No recorded activity in this period." />
          ) : (
            <div className="divide-y divide-stone/10">
              {by_category.map(cat => (
                <div key={cat.category} className="py-2.5">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-text-primary text-sm">{cat.category}</span>
                    <span className="text-sm font-semibold text-text-primary">{cat.total}</span>
                  </div>
                  {cat.actions.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      {cat.actions.map(a => (
                        <span key={a.action} className="text-xs bg-[#F5F5F5] text-text-secondary px-1.5 py-0.5 rounded">
                          {humanize(a.action)}: {a.count}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card title="Activity by organization">
          {by_organization.length === 0 ? (
            <Empty text="No recorded activity in this period." />
          ) : (
            <ul className="divide-y divide-stone/10">
              {by_organization.map(row => (
                <li key={row.organization_id ?? 'none'} className="py-2.5 flex items-center justify-between text-sm">
                  <span className="text-text-primary">{row.organization_name}</span>
                  <span className="font-semibold text-text-primary">{row.count}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <div className="mb-6">
        <Card title="Company activity (all users in guided companies)">
          <div className="text-xs text-text-muted mb-3">
            Company-wide counts across the companies this guide serves — not the guide's own actions.
            Meetings started are scoped to the selected period.
          </div>
          {company_rollup.organizations.length === 0 ? (
            <Empty text="Not currently guiding any companies." />
          ) : (
            <table className="w-full text-sm">
              <thead className="text-text-secondary">
                <tr>
                  <th className="text-left py-1.5 font-medium">Company</th>
                  <th className="text-right py-1.5 font-medium">Meetings started</th>
                  <th className="text-right py-1.5 font-medium">Active rocks</th>
                  <th className="text-right py-1.5 font-medium">Active users</th>
                </tr>
              </thead>
              <tbody>
                {company_rollup.organizations.map(org => (
                  <tr key={org.organization_id} className="border-t border-stone/10">
                    <td className="py-2 text-text-primary">{org.organization_name}</td>
                    <td className="py-2 text-right text-text-muted">{org.meetings_started}</td>
                    <td className="py-2 text-right text-text-muted">{org.active_rocks}</td>
                    <td className="py-2 text-right text-text-muted">{org.active_users}</td>
                  </tr>
                ))}
                <tr className="border-t border-stone/20 font-semibold">
                  <td className="py-2 text-text-primary">Total</td>
                  <td className="py-2 text-right text-text-primary">{company_rollup.totals.meetings_started}</td>
                  <td className="py-2 text-right text-text-primary">{company_rollup.totals.active_rocks}</td>
                  <td className="py-2 text-right text-text-primary">{company_rollup.totals.active_users}</td>
                </tr>
              </tbody>
            </table>
          )}
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Current state">
          <div className="text-xs text-text-muted mb-3">
            Live data, not dependent on audit history. Account created {formatDate(current_state.account_created_at)}.
          </div>
          {current_state.organizations.length === 0 ? (
            <Empty text="Not currently guiding any organizations." />
          ) : (
            <table className="w-full text-sm">
              <thead className="text-text-secondary">
                <tr>
                  <th className="text-left py-1.5 font-medium">Organization</th>
                  <th className="text-right py-1.5 font-medium">Action items</th>
                  <th className="text-right py-1.5 font-medium">Topics</th>
                  <th className="text-right py-1.5 font-medium">Rocks</th>
                </tr>
              </thead>
              <tbody>
                {current_state.organizations.map(org => (
                  <tr key={org.organization_id} className="border-t border-stone/10">
                    <td className="py-2 text-text-primary">
                      {org.organization_name}
                      {org.guide_user_privileges && (
                        <span className="ml-1.5 text-xs bg-charcoal/15 text-text-primary px-1.5 py-0.5 rounded-full">privileged</span>
                      )}
                    </td>
                    <td className="py-2 text-right text-text-muted">{org.open_action_items}</td>
                    <td className="py-2 text-right text-text-muted">{org.open_topics}</td>
                    <td className="py-2 text-right text-text-muted">{org.active_rocks}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card title="Recent activity">
          {recent_activity.length === 0 ? (
            <Empty text="No audit-log entries in this period." />
          ) : (
            <ul className="divide-y divide-stone/10 max-h-[28rem] overflow-y-auto">
              {recent_activity.map(item => (
                <li key={item.id} className="py-2.5 text-sm">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-text-primary">
                      <span className="font-medium">{humanize(item.action)}</span>
                      {item.target_name && <span className="text-text-muted"> · {item.target_name}</span>}
                    </span>
                    <span className="text-xs text-text-muted whitespace-nowrap">{formatDateTime(item.created_at)}</span>
                  </div>
                  {item.organization_name && (
                    <div className="text-xs text-text-muted mt-0.5">{item.organization_name}</div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </SuperAdminLayout>
  )
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="bg-white border border-stone/20 rounded-lg p-4">
      <div className="text-xs uppercase tracking-wide text-text-muted font-semibold">{label}</div>
      <div className="text-3xl font-bold text-text-primary mt-1">{value.toLocaleString()}</div>
    </div>
  )
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white border border-stone/20 rounded-lg p-4">
      <h2 className="text-lg font-semibold text-text-primary mb-3">{title}</h2>
      {children}
    </div>
  )
}

function Empty({ text }: { text: string }) {
  return <div className="py-6 text-center text-text-muted text-sm">{text}</div>
}
